import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Modal, Nav, Tab } from "react-bootstrap";
import { handleError, handleSuccess } from "../CommonJquery/CommonJquery";
import MasterLayout from "../masterLayout/MasterLayout";
import {
  connect_instagram_account,
  delete_instagram_comment,
  delete_instagram_media,
  get_instagram_account,
  get_instagram_comments,
  get_instagram_insights,
  get_instagram_media,
  get_instagram_media_insights,
  get_instagram_publish_limit,
  get_instagram_tagged_media,
  hide_instagram_comment,
  publish_instagram_media,
  reply_instagram_comment,
  save_instagram_account,
  search_instagram_hashtag,
  server_post_data,
  update_instagram_media,
  upload_public_media,
} from "../ServiceConnection/serviceconnection";
import Loader from "./Loader";
import "../components/CSS/gmb-premium.css";
import "../components/CSS/facebook-premium.css";
import "../components/CSS/instagram-premium.css";

/* ---------------------------------------------------------------------------
   Static config
--------------------------------------------------------------------------- */

const SOCIAL_PROFILES = [
  { key: "google", label: "Google Business", icon: "mdi:google", ready: false },
  { key: "facebook", label: "Facebook", icon: "mdi:facebook", ready: false },
  { key: "instagram", label: "Instagram", icon: "mdi:instagram", ready: true },
  { key: "twitter", label: "Twitter", icon: "ri:twitter-x-fill", ready: false },
  { key: "linkedin", label: "LinkedIn", icon: "mdi:linkedin", ready: false },
  { key: "pinterest", label: "Pinterest", icon: "mdi:pinterest", ready: false },
];

const CHANNEL_TABS = [
  { key: "instagram", label: "Instagram", ready: true },
  { key: "facebook", label: "Facebook", ready: false },
  { key: "google", label: "Google", ready: false },
  { key: "linkedin", label: "LinkedIn", ready: false },
  { key: "twitter", label: "Twitter", ready: false },
  { key: "pinterest", label: "Pinterest", ready: false },
];

const POST_TYPES = [
  { key: "IMAGE", label: "Image", icon: "mdi:image-outline" },
  { key: "REEL", label: "Reel", icon: "mdi:movie-play-outline" },
  { key: "CAROUSEL", label: "Carousel", icon: "mdi:image-multiple-outline" },
  { key: "STORY", label: "Story", icon: "mdi:cellphone" },
];

const TIPS = [
  {
    title: "Reels Reach Furthest",
    body: "Reels are pushed to non-followers far more aggressively than static posts — use them to grow, posts to convert.",
  },
  {
    title: "Front-Load Your Caption",
    body: "Only the first two lines show before the More link, so put the hook and the offer there.",
  },
  {
    title: "Use 3-5 Focused Hashtags",
    body: "A few tightly relevant tags outperform thirty broad ones and keep your posts out of spam-heavy feeds.",
  },
];

const emptyPost = {
  post_type: "IMAGE",
  caption: "",
  image_url: "",
  video_url: "",
  cover_url: "",
  location_id: "",
  share_to_feed: true,
};

const todayISO = () => new Date().toISOString().slice(0, 10);
const daysAgoISO = (n) =>
  new Date(Date.now() - n * 86400000).toISOString().slice(0, 10);

/* ------------------------------------------------------------------------ */

function InstagramPanel() {
  const [loading, setLoading] = useState(false);
  const [loadingNote, setLoadingNote] = useState("");
  const [activeTab, setActiveTab] = useState("account");

  // account
  const [account, setAccount] = useState(null);
  const [accountList, setAccountList] = useState([]);
  const [showAccountPicker, setShowAccountPicker] = useState(false);
  const [quota, setQuota] = useState(null);

  // media
  const [media, setMedia] = useState([]);
  const [confirmDelete, setConfirmDelete] = useState(null);

  // composer
  const [showComposer, setShowComposer] = useState(false);
  const [postForm, setPostForm] = useState(emptyPost);
  const [preview, setPreview] = useState("");
  const [carousel, setCarousel] = useState([]);
  const [channel, setChannel] = useState("instagram");

  // detail / comments / insights
  const [showDetail, setShowDetail] = useState(false);
  const [detailMedia, setDetailMedia] = useState(null);
  const [comments, setComments] = useState([]);
  const [replyBox, setReplyBox] = useState({});
  const [mediaInsights, setMediaInsights] = useState(null);

  // insights
  const [range, setRange] = useState({
    start_date: daysAgoISO(29),
    end_date: todayISO(),
    period: "day",
  });
  const [insights, setInsights] = useState(null);

  // tagged
  const [tagged, setTagged] = useState([]);

  // hashtags
  const [hashtag, setHashtag] = useState("");
  const [hashtagMode, setHashtagMode] = useState("top");
  const [hashtagResult, setHashtagResult] = useState(null);
  const hashtagCache = useRef({});

  const clickRef = useRef(0);

  /* ------------------------- common caller ------------------------- */
  const call = useCallback(async (url, fields = {}, files = {}) => {
    const fd = new FormData();
    Object.entries(fields).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") fd.append(k, v);
    });
    Object.entries(files).forEach(([k, f]) => f && fd.append(k, f));

    try {
      const res = await server_post_data(url, fd);
      const parts = res.message.split("~@~");
      if (parseInt(parts[0]) === 1) {
        handleError(parts[1]);
        return null;
      }
      const parsed = JSON.parse(parts[1]);
      if (parsed.error) {
        handleError(parsed.message || "Instagram API error");
        return null;
      }
      return parsed;
    } catch (e) {
      handleError("network");
      return null;
    }
  }, []);

  /* ------------------------- loaders ------------------------- */
  const loadAccount = useCallback(async () => {
    setLoading(true);
    const d = await call(get_instagram_account);
    if (d?.account) setAccount(d.account);
    setLoading(false);
  }, [call]);

  const loadQuota = useCallback(async () => {
    const d = await call(get_instagram_publish_limit);
    if (d) setQuota(d);
  }, [call]);

  const loadMedia = useCallback(async () => {
    setLoading(true);
    const d = await call(get_instagram_media, { limit: 50 });
    setMedia(d?.media || []);
    setLoading(false);
  }, [call]);

  const loadInsights = useCallback(async () => {
    setLoading(true);
    const d = await call(get_instagram_insights, range);
    setInsights(d?.insights || null);
    setLoading(false);
  }, [call, range]);

  const loadTagged = useCallback(async () => {
    setLoading(true);
    const d = await call(get_instagram_tagged_media, { limit: 30 });
    setTagged(d?.media || []);
    setLoading(false);
  }, [call]);

  useEffect(() => {
    loadAccount();
  }, [loadAccount]);

  useEffect(() => {
    if (activeTab === "media") loadMedia();
    if (activeTab === "insights") loadInsights();
    if (activeTab === "tagged") loadTagged();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  /* ------------------------- connect flow ------------------------- */
  const afterFbLogin = useCallback(
    async (accessToken) => {
      setLoading(true);
      const d = await call(connect_instagram_account, {
        user_access_token: accessToken,
      });
      setLoading(false);

      if (d?.accounts?.length) {
        setAccountList(
          d.accounts.map((a) => ({ ...a, userToken: d.longLivedUserToken })),
        );
        setShowAccountPicker(true);
      } else {
        handleError(
          "No Instagram Business account found. Link your Instagram account to a Facebook Page first.",
        );
      }
    },
    [call],
  );

  const handleConnect = () => {
    if (window.location.protocol !== "https:") {
      handleError(
        "Instagram login requires HTTPS. Please open this app over https://",
      );
      return;
    }
    if (!window.FB || typeof window.FB.login !== "function") {
      handleError("Facebook SDK is not ready yet. Please refresh the page.");
      return;
    }

    // Callback MUST be a plain (non-async) function — the SDK rejects async ones.
    window.FB.login(
      function (resp) {
        const token =
          resp && resp.authResponse && resp.authResponse.accessToken;
        if (!token) {
          handleError("Instagram login was cancelled");
          return;
        }
        afterFbLogin(token);
      },
      {
        scope:
          "pages_show_list,instagram_basic,instagram_content_publish," +
          "instagram_manage_comments,instagram_manage_insights,business_management",
        return_scopes: true,
      },
    );
  };

  const handleSelectAccount = async (a) => {
    setLoading(true);
    const d = await call(save_instagram_account, {
      ig_user_id: a.igUserId,
      page_id: a.pageId,
      page_access_token: a.accessToken,
      username: a.username,
      user_access_token: a.userToken,
    });
    setLoading(false);
    if (d) {
      handleSuccess("Instagram account connected successfully");
      setShowAccountPicker(false);
      loadAccount();
    }
  };

  /* ------------------------- media upload -> public URL ------------------------- */
  const uploadToPublicUrl = async (file) => {
    setLoadingNote("Uploading media...");
    setLoading(true);
    const d = await call(upload_public_media, {}, { media_file: file });
    setLoading(false);
    setLoadingNote("");
    return d; // { url, mediaKind }
  };

  const onPickMedia = async (file) => {
    if (!file) return;
    const d = await uploadToPublicUrl(file);
    if (!d) return;

    setPreview(d.url);
    if (d.mediaKind === "VIDEO") {
      setPostForm((s) => ({ ...s, video_url: d.url, image_url: "" }));
    } else {
      setPostForm((s) => ({ ...s, image_url: d.url, video_url: "" }));
    }
  };
  const compact = (n) => {
    if (n === null || n === undefined) return "—";
    if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
    if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
    return String(n);
  };

  const onPickCarouselItem = async (file) => {
    if (!file) return;
    if (carousel.length >= 10) {
      handleError("A carousel can hold at most 10 items");
      return;
    }
    const d = await uploadToPublicUrl(file);
    if (!d) return;
    setCarousel((s) => [...s, { type: d.mediaKind, url: d.url }]);
  };

  const clearMedia = () => {
    setPreview("");
    setPostForm((s) => ({ ...s, image_url: "", video_url: "" }));
  };

  /* ------------------------- publish ------------------------- */
  const resetComposer = () => {
    setPostForm(emptyPost);
    setPreview("");
    setCarousel([]);
    setChannel("instagram");
  };

  const openComposer = () => {
    resetComposer();
    loadQuota();
    setShowComposer(true);
  };

  const publish = async () => {
    const t = postForm.post_type;

    if (t === "CAROUSEL" && carousel.length < 2) {
      handleError("A carousel needs at least 2 items");
      return;
    }
    if (t !== "CAROUSEL" && !postForm.image_url && !postForm.video_url) {
      handleError("Please add an image or video first");
      return;
    }
    if (t === "REEL" && !postForm.video_url) {
      handleError("A Reel needs a video");
      return;
    }
    if (postForm.caption.length > 2200) {
      handleError("Caption cannot exceed 2200 characters");
      return;
    }

    if (clickRef.current === 1) return;
    clickRef.current = 1;
    setLoadingNote(
      postForm.video_url || t === "REEL"
        ? "Instagram is processing your video — this can take up to a minute."
        : "Publishing to Instagram...",
    );
    setLoading(true);

    const payload = {
      post_type: t,
      caption: postForm.caption,
      location_id: postForm.location_id,
    };
    if (t === "CAROUSEL") {
      payload.children = JSON.stringify(carousel);
    } else {
      payload.image_url = postForm.image_url;
      payload.video_url = postForm.video_url;
      if (t === "REEL") {
        payload.cover_url = postForm.cover_url;
        payload.share_to_feed = postForm.share_to_feed ? "1" : "0";
      }
    }

    const d = await call(publish_instagram_media, payload);

    setLoading(false);
    setLoadingNote("");
    clickRef.current = 0;

    if (d) {
      handleSuccess("Published to Instagram successfully");
      setShowComposer(false);
      loadMedia();
      loadQuota();
    }
  };

  const removeMedia = async () => {
    setLoading(true);
    const d = await call(delete_instagram_media, { media_id: confirmDelete });
    setLoading(false);
    setConfirmDelete(null);
    if (d) {
      handleSuccess("Media deleted successfully");
      setShowDetail(false);
      loadMedia();
    }
  };

  const toggleComments = async (m) => {
    setLoading(true);
    const d = await call(update_instagram_media, {
      media_id: m.mediaId,
      comment_enabled: m.commentsOff ? "1" : "0",
    });
    setLoading(false);
    if (d) handleSuccess(d.message || "Updated successfully");
  };

  /* ------------------------- detail / comments ------------------------- */
  const openDetail = async (m) => {
    setDetailMedia(m);
    setShowDetail(true);
    setComments([]);
    setMediaInsights(null);
    setLoading(true);

    const c = await call(get_instagram_comments, {
      media_id: m.mediaId,
      limit: 50,
    });
    setComments(c?.comments || []);

    const i = await call(get_instagram_media_insights, {
      media_id: m.mediaId,
      media_type: m.productType === "REELS" ? "REEL" : m.mediaType,
    });
    setMediaInsights(i?.insights || null);

    setLoading(false);
  };

  const sendReply = async (objectId, isCommentReply) => {
    const msg = (replyBox[objectId] || "").trim();
    if (!msg) return;
    setLoading(true);
    const d = await call(reply_instagram_comment, {
      object_id: objectId,
      message: msg,
      is_comment_reply: isCommentReply ? "1" : "0",
    });
    setLoading(false);
    if (d) {
      handleSuccess("Reply posted successfully");
      setReplyBox((s) => ({ ...s, [objectId]: "" }));
      openDetail(detailMedia);
    }
  };

  const removeComment = async (id) => {
    setLoading(true);
    const d = await call(delete_instagram_comment, { comment_id: id });
    setLoading(false);
    if (d) {
      handleSuccess("Comment deleted successfully");
      openDetail(detailMedia);
    }
  };

  const toggleHide = async (c) => {
    setLoading(true);
    const d = await call(hide_instagram_comment, {
      comment_id: c.id,
      hide: c.hidden ? "0" : "1",
    });
    setLoading(false);
    if (d) {
      handleSuccess(c.hidden ? "Comment unhidden" : "Comment hidden");
      openDetail(detailMedia);
    }
  };

  /* ------------------------- hashtags ------------------------- */
  const runHashtagSearch = async () => {
    const tag = hashtag.trim().replace(/^#/, "");
    if (!tag) {
      handleError("Please enter a hashtag");
      return;
    }
    setLoading(true);
    const d = await call(search_instagram_hashtag, {
      hashtag: tag,
      media_type: hashtagMode,
      limit: 12,
      hashtag_id: hashtagCache.current[tag] || "",
    });
    setLoading(false);
    if (d) {
      hashtagCache.current[tag] = d.hashtagId;
      setHashtagResult(d);
    }
  };

  /* ------------------------- helpers ------------------------- */
  const initials = (n) => (n || "?").trim().charAt(0).toUpperCase();

  const fmtDate = (d) =>
    d
      ? new Date(d).toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        })
      : "-";

  const typeIcon = (m) => {
    if (m.productType === "REELS") return "mdi:movie-play-outline";
    if (m.mediaType === "CAROUSEL_ALBUM") return "mdi:image-multiple-outline";
    if (m.mediaType === "VIDEO") return "mdi:play-circle-outline";
    return "mdi:image-outline";
  };

  const isCarousel = postForm.post_type === "CAROUSEL";
  const isReel = postForm.post_type === "REEL";

  return (
    <MasterLayout>
      <div className="gmb-premium fb-premium ig-premium">
        {loading && <Loader />}

        {/* ------------------------- Topbar ------------------------- */}
        <div className="gmb-topbar">
          <div className="gmb-topbar-title">
            <h5 className="gmb-display">Instagram</h5>
            <span className="gmb-royal-badge">
              <Icon icon="mdi:instagram" /> Social Suite
            </span>
            {account && (
              <span className="gmb-live">
                <span className="gmb-live-dot" /> Live
              </span>
            )}
          </div>
          <div className="gmb-topbar-actions">
            <button
              className="btn btn-outline-primary-600"
              onClick={handleConnect}
            >
              <Icon icon="mdi:account-switch-outline" /> Switch Account
            </button>
            <button className="btn btn-primary-600" onClick={openComposer}>
              <Icon icon="ic:baseline-plus" /> Create Post
            </button>
          </div>
        </div>

        {/* ------------------------- Account hero ------------------------- */}
        {account && (
          <div className="fb-page-hero">
            <div className="ig-hero-avatar">
              <img
                src={account.profile_picture_url || ""}
                alt={account.username}
                onError={(e) => (e.currentTarget.style.visibility = "hidden")}
              />
            </div>
            <div>
              <h6>@{account.username}</h6>
              <span>{account.name || "Instagram Business Account"}</span>
            </div>
            <div className="fb-page-stats">
              <div>
                <b className="gmb-mono">{account.followers_count ?? 0}</b>
                <small>Followers</small>
              </div>
              <div>
                <b className="gmb-mono">{account.follows_count ?? 0}</b>
                <small>Following</small>
              </div>
              <div>
                <b className="gmb-mono">{account.media_count ?? 0}</b>
                <small>Posts</small>
              </div>
            </div>
          </div>
        )}

        {/* ------------------------- Tabs ------------------------- */}
        <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
          <Nav className="nav-tabs">
            <Nav.Item>
              <Nav.Link eventKey="account">
                <Icon icon="mdi:information-outline" /> Profile
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="media">
                <Icon icon="mdi:grid" /> Posts
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="insights">
                <Icon icon="mdi:chart-line" /> Insights
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="tagged">
                <Icon icon="mdi:tag-outline" /> Tagged
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="hashtags">
                <Icon icon="mdi:pound" /> Hashtags
              </Nav.Link>
            </Nav.Item>
          </Nav>

          <Tab.Content>
            {/* ------------------ PROFILE ------------------ */}
            <Tab.Pane eventKey="account">
              {!account ? (
                <div className="gmb-empty">
                  No Instagram account is connected yet — use “Switch Account”
                  above. Your Instagram profile must be a Business or Creator
                  account linked to a Facebook Page.
                </div>
              ) : (
                <div className="gmb-card" style={{ padding: 20 }}>
                  <div className="gmb-detail-row">
                    <strong>Username</strong>
                    <span>@{account.username}</span>
                  </div>
                  <div className="gmb-detail-row">
                    <strong>Name</strong>
                    <span>{account.name || "-"}</span>
                  </div>
                  <div className="gmb-detail-row">
                    <strong>Biography</strong>
                    <span>{account.biography || "-"}</span>
                  </div>
                  <div className="gmb-detail-row">
                    <strong>Website</strong>
                    <span>
                      {account.website ? (
                        <a
                          href={account.website}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {account.website}
                        </a>
                      ) : (
                        "-"
                      )}
                    </span>
                  </div>
                  <div className="gmb-detail-row">
                    <strong>Followers</strong>
                    <span>{account.followers_count ?? 0}</span>
                  </div>
                  <div className="gmb-detail-row">
                    <strong>Total Posts</strong>
                    <span>{account.media_count ?? 0}</span>
                  </div>
                  <div className="gmb-detail-row">
                    <strong>Profile</strong>
                    <span>
                      <a
                        href={`https://www.instagram.com/${account.username}/`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        View on Instagram
                      </a>
                    </span>
                  </div>
                </div>
              )}
            </Tab.Pane>

            {/* ------------------ POSTS ------------------ */}
            <Tab.Pane eventKey="media">
              <div className="d-flex justify-content-end mb-3">
                <button
                  className="btn btn-outline-primary-600 btn-sm"
                  onClick={loadMedia}
                >
                  <Icon icon="mdi:refresh" /> Refresh
                </button>
              </div>

              {media.length === 0 ? (
                <div className="gmb-empty">No posts published yet.</div>
              ) : (
                <div className="row g-3">
                  {media.map((m) => (
                    <div
                      className="col-xl-2 col-lg-3 col-md-4 col-6"
                      key={m.mediaId}
                    >
                      <div
                        className="ig-grid-tile"
                        onClick={() => openDetail(m)}
                      >
                        <img src={m.thumbnailUrl} alt={m.caption} />
                        <span className="ig-grid-type">
                          <Icon icon={typeIcon(m)} />
                        </span>
                        <div className="ig-grid-overlay">
                          <span>
                            <Icon icon="mdi:heart" /> {m.likeCount}
                          </span>
                          <span>
                            <Icon icon="mdi:comment" /> {m.commentCount}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Tab.Pane>

            {/* ------------------ INSIGHTS ------------------ */}
            <Tab.Pane eventKey="insights">
              <div className="gmb-date-row">
                <div>
                  <label>Start Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={range.start_date}
                    onChange={(e) =>
                      setRange((s) => ({ ...s, start_date: e.target.value }))
                    }
                  />
                </div>
                <div>
                  <label>End Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={range.end_date}
                    onChange={(e) =>
                      setRange((s) => ({ ...s, end_date: e.target.value }))
                    }
                  />
                </div>
                <div>
                  <label>Period</label>
                  <select
                    className="form-control"
                    value={range.period}
                    onChange={(e) =>
                      setRange((s) => ({ ...s, period: e.target.value }))
                    }
                  >
                    <option value="day">Day</option>
                    <option value="week">Week</option>
                    <option value="days_28">28 Days</option>
                  </select>
                </div>
                <button className="btn btn-primary-600" onClick={loadInsights}>
                  <Icon icon="mdi:magnify" /> Fetch
                </button>
              </div>

              <div className="gmb-tab-sub">
                Instagram returns at most 30 days of account metrics per
                request.
              </div>

              {!insights ? (
                <div className="gmb-empty">
                  Select a date range and click Fetch to load metrics.
                </div>
              ) : (
                <div className="row g-3">
                  {Object.entries(insights).map(([key, m]) => (
                    <div className="col-xl-3 col-lg-4 col-md-6" key={key}>
                      <div className="gmb-card gmb-metric-card">
                        <div
                          className="gmb-metric-icon"
                          style={{
                            background: "#fde4ef",
                            color: "var(--ig-brand)",
                          }}
                        >
                          <Icon icon="mdi:chart-box-outline" />
                        </div>
                        <div className="gmb-metric-label">{m.title || key}</div>
                        <div className="gmb-metric-total gmb-mono">
                          {m.total}
                        </div>
                        <div className="gmb-metric-range">
                          <span>{range.start_date}</span>
                          <span>{range.end_date}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Tab.Pane>

            {/* ------------------ TAGGED ------------------ */}
            <Tab.Pane eventKey="tagged">
              <div className="gmb-tab-sub">
                Posts from other accounts that tagged you — great source of
                user-generated content to reshare.
              </div>
              {tagged.length === 0 ? (
                <div className="gmb-empty">No tagged posts found.</div>
              ) : (
                <div className="row g-3">
                  {tagged.map((m) => (
                    <div
                      className="col-xl-2 col-lg-3 col-md-4 col-6"
                      key={m.id}
                    >
                      <div className="ig-grid-tile">
                        <img src={m.thumbnail_url || m.media_url} alt="" />

                        <a
                          className="ig-grid-overlay"
                          href={m.permalink}
                          target="_blank"
                          rel="noreferrer"
                          style={{ textDecoration: "none", color: "#fff" }}
                        >
                          <span>@{m.username}</span>
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Tab.Pane>

            {/* ------------------ HASHTAGS ------------------ */}
            <Tab.Pane eventKey="hashtags">
              <div className="ig-hashtag-search">
                <div>
                  <label>Hashtag</label>
                  <input
                    className="form-control"
                    placeholder="Eg: streetfood"
                    value={hashtag}
                    onChange={(e) => setHashtag(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && runHashtagSearch()}
                  />
                </div>
                <div>
                  <label>Sort</label>
                  <select
                    className="form-control"
                    value={hashtagMode}
                    onChange={(e) => setHashtagMode(e.target.value)}
                  >
                    <option value="top">Top Posts</option>
                    <option value="recent">Most Recent</option>
                  </select>
                </div>
                <button
                  className="btn btn-primary-600"
                  onClick={runHashtagSearch}
                >
                  <Icon icon="mdi:magnify" /> Search
                </button>
              </div>

              <div className="ig-quota-note">
                Instagram allows 30 unique hashtag searches per week, so
                previously searched tags are reused from cache.
              </div>

              {!hashtagResult ? (
                <div className="gmb-empty">
                  Search a hashtag to see what's performing in your niche.
                </div>
              ) : hashtagResult.media.length === 0 ? (
                <div className="gmb-empty">
                  No public posts returned for #{hashtagResult.hashtag}.
                </div>
              ) : (
                <div className="row g-3 mt-1">
                  {hashtagResult.media.map((m) => (
                    <div
                      className="col-xl-2 col-lg-3 col-md-4 col-6"
                      key={m.id}
                    >
                      <div className="ig-grid-tile">
                        <img src={m.thumbnailUrl} alt="" />

                        <a
                          className="ig-grid-overlay"
                          href={m.permalink}
                          target="_blank"
                          rel="noreferrer"
                          style={{ textDecoration: "none", color: "#fff" }}
                        >
                          <span>
                            <Icon icon="mdi:heart" /> {compact(m.likeCount)}
                          </span>
                          <span>
                            <Icon icon="mdi:comment" />{" "}
                            {compact(m.commentsCount)}
                          </span>
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Tab.Pane>
          </Tab.Content>
        </Tab.Container>

        {/* ==================== COMPOSER MODAL ==================== */}
        <Modal
          className="gmb-modal fb-composer-modal"
          size="xl"
          centered
          show={showComposer}
          onHide={() => setShowComposer(false)}
        >
          <Modal.Body>
            <div className="fb-composer">
              {/* ---------- LEFT: social profiles ---------- */}
              <div className="fb-composer-col fb-composer-rail">
                <div className="fb-composer-heading">
                  Select Social Profiles
                </div>

                {SOCIAL_PROFILES.map((sp) => {
                  const connected = sp.key === "instagram" && !!account;
                  return (
                    <div
                      key={sp.key}
                      className={`fb-social-item ${sp.key} ${
                        connected ? "is-connected" : ""
                      }`}
                    >
                      <div className="fb-social-head">
                        <Icon icon={sp.icon} />
                        {sp.label}
                        {connected && (
                          <span className="fb-social-count">(1)</span>
                        )}
                      </div>
                      <div className="fb-social-foot">
                        <button
                          type="button"
                          disabled={!sp.ready}
                          onClick={sp.ready ? handleConnect : undefined}
                        >
                          {connected ? "Connected" : "Connect Account"}
                        </button>
                        <span className="fb-social-status">
                          <Icon
                            icon={
                              connected
                                ? "mdi:check-circle"
                                : "mdi:check-circle-outline"
                            }
                          />
                        </span>
                      </div>
                    </div>
                  );
                })}

                <div className="fb-rail-divider" />

                {quota && (
                  <>
                    <div className="ig-quota">
                      <span className="ig-quota-label">Daily Quota</span>
                      <span className="ig-quota-track">
                        <span
                          className="ig-quota-fill"
                          style={{
                            width: `${Math.min(
                              (quota.quotaUsed / quota.quotaTotal) * 100,
                              100,
                            )}%`,
                          }}
                        />
                      </span>
                      <span className="ig-quota-value">
                        {quota.quotaRemaining} left
                      </span>
                    </div>
                    <div className="gmb-char-count">
                      Instagram allows {quota.quotaTotal} API posts per 24
                      hours.
                    </div>
                  </>
                )}

                <div className="fb-toggle-row mt-3">
                  <span>Scheduling</span>
                  <button type="button" className="fb-switch" disabled />
                </div>
                <div className="gmb-char-count">
                  Instagram's API has no native scheduling — posts publish
                  immediately.
                </div>
              </div>

              {/* ---------- MIDDLE: editor ---------- */}
              <div className="fb-composer-col">
                <div className="fb-composer-title">Create Post</div>
                <div className="fb-composer-sub">
                  {account
                    ? `@${account.username} selected`
                    : "No account selected"}
                </div>

                <div className="fb-channel-tabs">
                  {CHANNEL_TABS.map((c) => (
                    <button
                      key={c.key}
                      type="button"
                      disabled={!c.ready}
                      className={channel === c.key ? "active" : ""}
                      onClick={() => c.ready && setChannel(c.key)}
                    >
                      {c.label}
                    </button>
                  ))}
                </div>

                <div className="fb-account-row">
                  <Icon
                    icon="mdi:instagram"
                    style={{ color: "var(--ig-brand)" }}
                  />
                  Instagram Account:
                  <span className="fb-account-chip">
                    {account ? `@${account.username}` : "Not connected"}
                  </span>
                </div>

                <div className="fb-field-title">Select Post Type</div>
                <div className="fb-pills">
                  {POST_TYPES.map((t) => (
                    <button
                      key={t.key}
                      type="button"
                      className={`fb-pill ${
                        postForm.post_type === t.key ? "active" : ""
                      }`}
                      onClick={() => {
                        setPostForm((s) => ({
                          ...s,
                          post_type: t.key,
                          image_url: "",
                          video_url: "",
                        }));
                        setPreview("");
                        setCarousel([]);
                      }}
                    >
                      {postForm.post_type === t.key ? (
                        <Icon icon="mdi:check" />
                      ) : (
                        <Icon icon={t.icon} />
                      )}
                      {t.label}
                    </button>
                  ))}
                </div>

                {/* ---- Carousel builder ---- */}
                {isCarousel ? (
                  <>
                    <div className="fb-field-title">
                      Carousel Items ( 2 - 10 items, square 1080 x 1080 )
                    </div>
                    <div className="ig-carousel-list">
                      {carousel.map((c, i) => (
                        <div className="ig-carousel-item" key={c.url + i}>
                          {c.type === "VIDEO" ? (
                            <video src={c.url} muted />
                          ) : (
                            <img src={c.url} alt="" />
                          )}
                          <button
                            type="button"
                            onClick={() =>
                              setCarousel((s) =>
                                s.filter((_, idx) => idx !== i),
                              )
                            }
                          >
                            <Icon icon="mdi:close" />
                          </button>
                        </div>
                      ))}
                      {carousel.length < 10 && (
                        <label className="ig-carousel-add">
                          <input
                            type="file"
                            accept="image/*,video/*"
                            onChange={(e) =>
                              onPickCarouselItem(e.target.files[0])
                            }
                          />
                          <Icon icon="mdi:plus" />
                        </label>
                      )}
                    </div>
                    <div className="gmb-char-count mt-2">
                      {carousel.length} of 10 items added
                    </div>
                  </>
                ) : (
                  <>
                    <div className="fb-field-title">
                      Add {isReel ? "Reel Video" : "Post Media"} (
                      {isReel ? " 9:16 vertical " : " Recommended 1080 X 1080 "}
                      )
                    </div>

                    {preview ? (
                      <div className="fb-drop-preview">
                        {postForm.video_url ? (
                          <video src={preview} controls />
                        ) : (
                          <img src={preview} alt="preview" />
                        )}
                        <button
                          type="button"
                          className="fb-drop-remove"
                          onClick={clearMedia}
                        >
                          <Icon icon="mdi:close" />
                        </button>
                      </div>
                    ) : (
                      <label className="fb-dropzone">
                        <input
                          type="file"
                          accept={isReel ? "video/*" : "image/*,video/*"}
                          onChange={(e) => onPickMedia(e.target.files[0])}
                        />
                        <div className="fb-drop-icon">
                          <Icon icon="mdi:image-plus-outline" />
                        </div>
                        <div className="fb-drop-title">
                          Upload or Drag {isReel ? "Video" : "Photo or Video"}
                        </div>
                        <div className="fb-drop-hint">
                          Maximum Size - 100 MB. Format JPG, PNG, MP4, MOV
                        </div>
                      </label>
                    )}
                  </>
                )}

                {isReel && (
                  <div className="d-flex align-items-center gap-2 mt-3">
                    <input
                      type="checkbox"
                      id="igShareToFeed"
                      checked={postForm.share_to_feed}
                      onChange={(e) =>
                        setPostForm((s) => ({
                          ...s,
                          share_to_feed: e.target.checked,
                        }))
                      }
                    />
                    <label
                      htmlFor="igShareToFeed"
                      className="gmb-field-label mb-0"
                    >
                      Also show this Reel on my profile grid
                    </label>
                  </div>
                )}

                {postForm.post_type !== "STORY" && (
                  <>
                    <div className="fb-field-title mt-4">Write a Caption</div>
                    <textarea
                      className="form-control"
                      rows={5}
                      placeholder="Eg: New Product Launch, Happy Customer Post #yourbrand"
                      value={postForm.caption}
                      onChange={(e) =>
                        setPostForm((s) => ({ ...s, caption: e.target.value }))
                      }
                    />
                    <div className="gmb-char-count">
                      {postForm.caption.length} / 2200 characters
                    </div>
                  </>
                )}

                <div className="fb-submit-bar">
                  <button
                    type="button"
                    className="btn btn-outline-primary-600"
                    onClick={() => setShowComposer(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="fb-submit"
                    disabled={!account}
                    onClick={publish}
                  >
                    <Icon icon="mdi:instagram" />
                    Publish To Instagram
                  </button>
                </div>
              </div>

              {/* ---------- RIGHT: preview + tips ---------- */}
              <div className="fb-composer-col fb-composer-aside">
                <div className="fb-preview">
                  <div className="fb-preview-head">
                    <Icon
                      icon="mdi:instagram"
                      style={{ color: "var(--ig-brand)" }}
                    />
                    Instagram Preview
                  </div>

                  <div className="ig-preview-bar">
                    <span className="ig-preview-dot" />
                    <b>{account ? account.username : "yourbrand"}</b>
                  </div>

                  {preview || carousel[0]?.url ? (
                    postForm.video_url && !isCarousel ? (
                      <video
                        className="ig-preview-square"
                        src={preview}
                        muted
                      />
                    ) : (
                      <img
                        className="ig-preview-square"
                        src={preview || carousel[0]?.url}
                        alt=""
                      />
                    )
                  ) : (
                    <div className="ig-preview-empty">
                      <Icon icon="mdi:image-outline" style={{ fontSize: 34 }} />
                      No Media Added
                    </div>
                  )}

                  <div className="ig-preview-actions">
                    <Icon icon="mdi:heart-outline" />
                    <Icon icon="mdi:comment-outline" />
                    <Icon icon="mdi:send-outline" />
                  </div>

                  <div className="fb-preview-body">
                    <p>
                      {postForm.caption ||
                        (postForm.post_type === "STORY"
                          ? "Stories don't use captions"
                          : "Your caption here")}
                    </p>
                  </div>
                </div>

                <div className="fb-composer-heading">Tips to Improve Reach</div>
                {TIPS.map((t) => (
                  <div className="fb-tip-card" key={t.title}>
                    <div className="fb-tip-icon">
                      <Icon icon="mdi:lightbulb-on-outline" />
                    </div>
                    <h6>{t.title}</h6>
                    <p>{t.body}</p>
                  </div>
                ))}
              </div>
            </div>
          </Modal.Body>
        </Modal>

        {/* ==================== MEDIA DETAIL ==================== */}
        <Modal
          className="gmb-modal"
          size="lg"
          centered
          show={showDetail}
          onHide={() => setShowDetail(false)}
        >
          <Modal.Header closeButton className="border-0 pb-0">
            <Modal.Title>Post Details</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {detailMedia && (
              <>
                <div className="row g-3 mb-3">
                  <div className="col-md-5">
                    <img
                      src={detailMedia.thumbnailUrl}
                      alt=""
                      style={{
                        width: "100%",
                        borderRadius: 10,
                        border: "1px solid var(--gmb-line)",
                      }}
                    />
                  </div>
                  <div className="col-md-7">
                    <p className="gmb-review-comment mt-0">
                      {detailMedia.caption || "(no caption)"}
                    </p>
                    <div className="gmb-metric-range mb-3">
                      <span>{fmtDate(detailMedia.timestamp)}</span>

                      <a
                        className="gmb-competitor-maps-link"
                        href={detailMedia.permalink}
                        target="_blank"
                        rel="noreferrer"
                      >
                        View on Instagram
                      </a>
                    </div>

                    {mediaInsights && (
                      <div className="row g-2">
                        {Object.entries(mediaInsights).map(([k, v]) => (
                          <div className="col-6" key={k}>
                            <div className="gmb-metric-label">
                              {v.title || k}
                            </div>
                            <div
                              className="gmb-mono"
                              style={{ fontWeight: 800 }}
                            >
                              {typeof v.value === "object"
                                ? JSON.stringify(v.value)
                                : v.value}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="d-flex gap-2 mt-3">
                      <button
                        className="btn btn-outline-primary-600 btn-sm"
                        onClick={() => toggleComments(detailMedia)}
                      >
                        <Icon icon="mdi:comment-off-outline" /> Toggle Comments
                      </button>
                      <button
                        className="btn btn-outline-danger-600 btn-sm"
                        onClick={() => setConfirmDelete(detailMedia.mediaId)}
                      >
                        <Icon icon="fluent:delete-24-regular" /> Delete
                      </button>
                    </div>
                  </div>
                </div>

                <div className="gmb-section-title mb-2">Comments</div>
                <div className="d-flex gap-2 mb-3">
                  <input
                    className="form-control"
                    placeholder="Write a comment on this post..."
                    value={replyBox[detailMedia.mediaId] || ""}
                    onChange={(e) =>
                      setReplyBox((s) => ({
                        ...s,
                        [detailMedia.mediaId]: e.target.value,
                      }))
                    }
                  />
                  <button
                    className="btn btn-primary-600"
                    onClick={() => sendReply(detailMedia.mediaId, false)}
                  >
                    Post
                  </button>
                </div>

                {comments.length === 0 ? (
                  <div className="gmb-empty">No comments yet.</div>
                ) : (
                  comments.map((c) => (
                    <div key={c.id}>
                      <div className="fb-comment">
                        <div className="fb-comment-avatar">
                          {initials(c.username)}
                        </div>
                        <div className="fb-comment-body">
                          <b>@{c.username}</b>
                          <p>{c.text}</p>
                          <div className="fb-comment-actions">
                            <button
                              onClick={() =>
                                setReplyBox((s) => ({
                                  ...s,
                                  [c.id]: s[c.id] || "",
                                }))
                              }
                            >
                              Reply
                            </button>
                            <button onClick={() => toggleHide(c)}>
                              {c.hidden ? "Unhide" : "Hide"}
                            </button>
                            <button
                              className="danger"
                              onClick={() => removeComment(c.id)}
                            >
                              Delete
                            </button>
                          </div>
                          {replyBox[c.id] !== undefined && (
                            <div className="d-flex gap-2 mt-2">
                              <input
                                className="form-control"
                                placeholder="Write a reply..."
                                value={replyBox[c.id]}
                                onChange={(e) =>
                                  setReplyBox((s) => ({
                                    ...s,
                                    [c.id]: e.target.value,
                                  }))
                                }
                              />
                              <button
                                className="btn btn-primary-600 btn-sm"
                                onClick={() => sendReply(c.id, true)}
                              >
                                Send
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                      {((c.replies || {}).data || []).map((r) => (
                        <div className="fb-comment fb-comment-reply" key={r.id}>
                          <div className="fb-comment-avatar">
                            {initials(r.username)}
                          </div>
                          <div className="fb-comment-body">
                            <b>@{r.username}</b>
                            <p>{r.text}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ))
                )}
              </>
            )}
          </Modal.Body>
        </Modal>

        {/* ==================== ACCOUNT PICKER ==================== */}
        <Modal
          className="gmb-modal"
          centered
          show={showAccountPicker}
          onHide={() => setShowAccountPicker(false)}
        >
          <Modal.Header closeButton className="border-0 pb-0">
            <Modal.Title>Select an Instagram Account</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {accountList.map((a) => (
              <div
                className="gmb-card gmb-location-card mb-2 d-flex align-items-center gap-3"
                key={a.igUserId}
                style={{ cursor: "pointer" }}
                onClick={() => handleSelectAccount(a)}
              >
                <img
                  src={a.profilePicture}
                  alt=""
                  style={{ width: 42, height: 42, borderRadius: "50%" }}
                />
                <div>
                  <h6 className="mb-0">@{a.username}</h6>
                  <div className="gmb-location-meta">
                    {a.followersCount} followers · via {a.pageName}
                  </div>
                </div>
              </div>
            ))}
          </Modal.Body>
        </Modal>

        {/* ==================== DELETE CONFIRM ==================== */}
        <Modal
          className="gmb-modal"
          size="sm"
          centered
          show={!!confirmDelete}
          onHide={() => setConfirmDelete(null)}
        >
          <Modal.Body className="text-center">
            <Icon
              icon="fluent:delete-24-regular"
              style={{ fontSize: 34, color: "var(--danger-main)" }}
            />
            <p className="mt-2 mb-0">
              Please confirm, do you want to delete this post?
            </p>
          </Modal.Body>
          <Modal.Footer>
            <button
              className="btn btn-outline-primary-600"
              onClick={() => setConfirmDelete(null)}
            >
              Cancel
            </button>
            <button className="btn btn-danger-600" onClick={removeMedia}>
              Confirm
            </button>
          </Modal.Footer>
        </Modal>
      </div>
    </MasterLayout>
  );
}

export default InstagramPanel;
